import Css from "./style.module.scss";

import { checkIsBusinessUser, getUserData } from "selectors/user";
import {
  checkSelectedBusinessHasBusinessUsers,
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getGlobalStats,
  getSelectedBusinessData
} from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Arrays from "utils/Arrays";
import AskClientPopup from "nlib/common/AskClientPopup";
import BulkActions from "nlib/common/TransactionsTable/lib/BulkActions";
import Businesses from "utils/Businesses";
import DataStatuses from "const/DataStatuses";
import EmptyState from "./lib/EmptyState";
import Filters from "./lib/Filters";
import MonthsTabs from "nlib/common/MonthsTabs";
import Page from "nlib/common/Page";
import PageContent from "nlib/common/PageContent";
import PageHeader from "nlib/common/PageHeader";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import RpaDetailsPanel from "nlib/common/RpaDetailsPanel";
import Texts from "utils/Texts";
import TransactionsActions from "actions/TransactionsActions";
import TransactionsContext from "contexts/TransactionsContext";
import TransactionsIntroModal from "nlib/common/TransactionsIntroModal";
import TransactionsPopup from "nlib/common/TransactionsPopup";
import TransactionsStatusFilter from "nlib/common/TransactionsStatusFilter";
import TransactionsTable from "./lib/TransactionsTable";
import TransactionsTableBusiness from "./lib/TransactionsTableBusiness";
import UpsellModal from "nlib/common/UpsellModal";
import UserActions from "actions/UserActions";
import UserRoles from "const/UserRoles";
import XeroTransactionsPlaceholder from "nlib/common/XeroTransactionsPlaceholder";
import moment from "moment";
import useAvailableWidth from "hooks/useAvailableWidth";
import useEnvVars from "hooks/useEnvVars";
import useTransactions from "hooks/useTransactions";

const SIMPLIFIED_LAYOUT_MAX_WIDTH = 900;

const RPA_SIMPLIFIED_LAYOUT_MIN_WIDTH = 1100;

const TransactionsPage = () => {
  const dispatch = useDispatch();

  const [envVars, setEnvVars] = useEnvVars();

  const { uiTexts, messages } = useSelector(getTextsData);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const businessUser = useSelector(checkIsBusinessUser);

  const globalStats = useSelector(getGlobalStats);

  const selectedBusinessHasBusinessUsers = useSelector(checkSelectedBusinessHasBusinessUsers);

  const {
    createdAt,
    id: businessId,
    settings: {
      rpaStartFrom
    } = {},
    extraData: {
      integrationService,
      transactionsLastSyncedAt,
      rpaUpsellDate,
      uncategorizedRpaEnabled,
      quickBooksBookCloseDate,
      quickBooksOldestTransactionDate
    } = {}
  } = useSelector(getSelectedBusinessData);

  const { role: userRole, guestUser, seenContent = {} } = useSelector(getUserData);

  const rpaEnabled = useSelector(checkSelectedBusinessRpaMode);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const {
    fetching,
    popupMode,
    transactionsForPopup = [],
    selectedTransactions,
    selectedTransactionsData,
    editableTransactionsIds,
    transactionsReadyToProcess,
    transactionsReadyToReview,
    transactionsReadyToApprove,
    selectedNeedReactionIds,
    closedBookDateTransactionIds,
    initialFetchDone,
    transactionsFetching,
    transactionsData,
    transactionsState,
    setTransactionsState,
    refetchTransactions,
    setSelectedTransactions,
    onSelectedChange,
    onAskClientPopupClose,
    onBulkActionsEdit,
    onBulkActionsCancel,
    onTransactionsPopupSubmit
  } = useTransactions();

  const { containerRef, availableWidth } = useAvailableWidth([envVars.editItem, envVars.transactionId, transactionsFetching]);

  const [localReasons, setLocalReasons] = useState([]);

  const [upsellModalShown, setUpsellModalShown] = useState(false);

  const selectedTransactionsUsesItems = useMemo(() => {
    return selectedTransactionsData.every(({ usesItems }) => usesItems);
  }, [selectedTransactionsData]);

  const byMonthData = useMemo(() => {
    return [
      DataStatuses.NEED_REACTION,
      DataStatuses.TO_RECONCILE,
      DataStatuses.TO_REPORT,
      DataStatuses.TO_REVIEW,
      DataStatuses.TO_REPORT,
      DataStatuses.EXPORTED
    ].reduce((result, status) => {
      const sectionData = (globalStats.transactions || {})[status] || {};

      if (sectionData.byMonth) {
        Object.entries(sectionData.byMonth).forEach(([month, { count, comments }]) => {
          const { count: prevCount = 0, comments: prevComments = 0 } = result[month] || {};

          result[month] = { count: count + prevCount, comments: comments + prevComments };
        });
      }

      return result;
    }, {});
  }, [globalStats.transactions]);

  const providedContext = useMemo(() => {
    return {
      transactionsData,
      transactionsState,
      setTransactionsState,
      refetchTransactions,
      localReasons,
      setLocalReasons
    };
  }, [
    localReasons,
    refetchTransactions,
    setTransactionsState,
    transactionsData,
    transactionsState
  ]);

  const accountantUser = useMemo(() => {
    return UserRoles.checkIsAccountant(userRole);
  }, [userRole]);

  const simplifyLayout = useMemo(() => {
    return availableWidth <= (rpaEnabled ? RPA_SIMPLIFIED_LAYOUT_MIN_WIDTH : SIMPLIFIED_LAYOUT_MAX_WIDTH);
  }, [availableWidth, rpaEnabled]);

  const allItemsSelected = useMemo(() => {
    return !!editableTransactionsIds.length
      && editableTransactionsIds.every((id) => selectedTransactions.includes(id));
  }, [editableTransactionsIds, selectedTransactions]);

  const subTitle = useMemo(() => {
    return transactionsLastSyncedAt && !businessUser
      ? Texts.replaceVars(
        uiTexts.lastSyncDate,
        { date: moment.duration(moment.utc(transactionsLastSyncedAt).diff(moment.utc())).humanize(true) }
      )
      : null;
  }, [transactionsLastSyncedAt, businessUser, uiTexts.lastSyncDate]);

  const handleSelectAllChange = useCallback(() => {
    setSelectedTransactions(allItemsSelected ? [] : editableTransactionsIds);
  }, [allItemsSelected, editableTransactionsIds, setSelectedTransactions]);

  const handleReasonBlur = useCallback(() => {
    const reasons = transactionsState.map((item) => item.reason).filter(Boolean);

    setLocalReasons(() => [...new Set(reasons)]);
  }, [transactionsState, setLocalReasons]);

  const handleMonthTabChange = useCallback((params) => {
    if (params.fromDate !== envVars.fromDate || params.toDate !== envVars.toDate) {
      setEnvVars({ ...params, text: null, type: null, accountId: null });
    } else {
      refetchTransactions();
    }
  }, [setEnvVars, envVars.fromDate, envVars.toDate, refetchTransactions]);

  const handleStatusTabClick = useCallback((value) => {
    if (envVars.status === value) refetchTransactions();
  }, [envVars.status, refetchTransactions]);

  const handleUpsellModalClose = useCallback(() => {
    setUpsellModalShown(false);
    if (seenContent.upsellPromoWindow) return;
    dispatch(UserActions.editUser({
      seenContent: { upsellPromoWindow: true },
      backgroundUpdate: true,
      silentUpdate: true
    }));
  }, [seenContent.upsellPromoWindow, dispatch]);

  useEffect(() => {
    setSelectedTransactions((prevState) => {
      return prevState.filter((id) => Arrays.findById(transactionsData, id));
    });
  }, [setSelectedTransactions, transactionsData]);
  useEffect(() => {
    const reasons = [...new Set(transactionsData.map((itemState) => itemState.reason).filter(Boolean))];

    if (reasons.length) {
      dispatch(TransactionsActions.fetchTransactionsReasonsList(reasons, true));
    }
  }, [transactionsData, dispatch]);
  useEffect(() => {
    const shouldShowUpsell = !seenContent.upsellPromoWindow
      && quickBooksBusiness
      && accountantUser
      && initialFetchDone
      && Businesses.checkUpsellAvailable({
        createdAt,
        integrationService,
        rpaStartFrom,
        rpaUpsellDate,
        uncategorizedRpaEnabled,
        quickBooksOldestTransactionDate,
        quickBooksBookCloseDate
      });

    if (!shouldShowUpsell) return;
    setUpsellModalShown(true);
  }, [
    accountantUser,
    initialFetchDone,
    createdAt,
    integrationService,
    rpaStartFrom,
    rpaUpsellDate,
    uncategorizedRpaEnabled,
    quickBooksBusiness,
    quickBooksOldestTransactionDate,
    quickBooksBookCloseDate,
    seenContent.upsellPromoWindow
  ]);
  useEffect(() => {
    setUpsellModalShown(false);
  }, [businessId]);

  const TableComponent = businessUser ? TransactionsTableBusiness : TransactionsTable;

  if (xeroBusiness && !rpaEnabled) {
    return (
      <Page className={Css.transactionsPage} scrollTop={`${envVars.page}.${envVars.pageSize}`}>
        <XeroTransactionsPlaceholder />
      </Page>
    );
  }

  return (
    <TransactionsContext.Provider value={providedContext}>
      <Page className={Css.transactionsPage} scrollTop={`${envVars.page}.${envVars.pageSize}`}>
        <PageContent ref={containerRef}>
          <PageHeader subtitle={subTitle} className={Css.pageHeader} />
          <div className={Css.statusFilters}>
            {guestUser
              ? <div className={Css.description}>{messages.accountantAsksUserDescription}</div>
              : (
                <>
                  <MonthsTabs
                    disabled={transactionsFetching}
                    data={byMonthData}
                    value={`${envVars.fromDate}-${envVars.toDate}`}
                    dependencies={[envVars.transactionId]}
                    onChange={handleMonthTabChange} />
                  <TransactionsStatusFilter
                    disabled={transactionsFetching}
                    simplifyLayout={simplifyLayout}
                    onClick={handleStatusTabClick} />
                </>
              )}
          </div>
          {!!selectedTransactions.length && (
            <BulkActions
              disabled={transactionsFetching}
              simplifyLayout={simplifyLayout}
              usesItems={selectedTransactionsUsesItems}
              selectedTransactions={selectedTransactions}
              selectedTransactionsData={selectedTransactionsData}
              localReasons={localReasons}
              onReasonBlur={handleReasonBlur}
              onCancel={onBulkActionsCancel}
              onEdit={onBulkActionsEdit} />
          )}
          <Filters
            disabled={fetching || transactionsFetching}
            compact={simplifyLayout}
            className={Css.filters} />
          <TableComponent
            disabled={fetching || transactionsFetching}
            simplifyLayout={simplifyLayout}
            localReasons={localReasons}
            allItemsSelected={allItemsSelected}
            editableTransactionsIds={editableTransactionsIds}
            selectedTransactions={selectedTransactions}
            transactionsReadyToReview={transactionsReadyToReview}
            transactionsReadyToProcess={transactionsReadyToProcess}
            transactionsReadyToApprove={transactionsReadyToApprove}
            closedBookDateTransactionIds={closedBookDateTransactionIds}
            availableWidth={availableWidth}
            onReasonBlur={handleReasonBlur}
            onSelectedChange={onSelectedChange}
            onSelectAllChange={handleSelectAllChange} />
          {(!businessOrganization || selectedBusinessHasBusinessUsers) && (
            <AskClientPopup
              disabled={fetching || transactionsFetching}
              statusFilter={envVars.status}
              transactionIds={selectedNeedReactionIds}
              onClose={onAskClientPopupClose} />
          )}
          {!transactionsData.length && !!transactionsState ? (
            <EmptyState fetchingData={transactionsFetching} />
          ) : (
            <TransactionsPopup
              disabled={fetching || transactionsFetching}
              popupMode={popupMode}
              transactionsForPopup={transactionsForPopup}
              onSubmit={onTransactionsPopupSubmit} />
          )}
        </PageContent>
      </Page>
      {envVars.transactionId && <RpaDetailsPanel />}
      <TransactionsIntroModal />
      {upsellModalShown && <UpsellModal onClose={handleUpsellModalClose} />}
    </TransactionsContext.Provider>
  );
};

export default React.memo(TransactionsPage);
